// Helpers for the jalur-3 (presigned) direct-to-Spaces upload flow.
//
// The dashboard image widgets emit a base64 `data:` URL (already resized). To
// PUT the file straight to DigitalOcean Spaces we need a real binary Blob plus
// the extension/content-type to request the presigned URL with.
//
// Pure & framework-free → safe to unit test in isolation.

/**
 * Convert a `data:image/...;base64,...` URL into a Blob for direct PUT.
 * @param {string} dataUrl
 * @returns {{ blob: Blob, extension: string, contentType: string }}
 */
export function dataUrlToBlob(dataUrl) {
  const str = String(dataUrl || "");
  const [meta, b64] = str.split(";base64,");
  const contentType = (meta || "").replace("data:", "") || "application/octet-stream";
  // "image/svg+xml" → "svg"; "image/jpeg" → "jpeg"
  const extension = ((contentType.split("/")[1] || "png").split("+")[0] || "png").toLowerCase();

  const binary = atob(b64 || "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  return { blob: new Blob([bytes], { type: contentType }), extension, contentType };
}

/** True when a form field holds a fresh base64 image that must be uploaded. */
export function isFreshDataUrl(value) {
  return typeof value === "string" && value.startsWith("data:image/");
}

/** Max SVG size accepted (vektor biasanya kecil; batas menghindari SVG raksasa). */
export const SVG_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Read a picked SVG File as a `data:image/svg+xml;base64,...` URL, bypassing the
 * raster resize widget (canvas would rasterize/rusak vektor). Rejects non-SVG or
 * oversize files so the caller can surface a message.
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readSvgFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("no-file"));
    const isSvg = file.type === "image/svg+xml" || /\.svg$/i.test(file.name || "");
    if (!isSvg) return reject(new Error("not-svg"));
    if (file.size > SVG_MAX_BYTES) return reject(new Error("too-big"));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error("read-failed"));
    reader.readAsDataURL(file);
  });
}
